// Pure helpers for the Finance accounts header. No React and no Graph calls:
// Finance.js loads Finance_Balances / Finance_Transactions with the user's
// token and hands the rows in.

// Tiles are keyed by account mask (Finance_Balances.Mask).
export const CASH_MASKS   = ['2634', '2564', '1758', '9650'];
export const INVEST_MASKS = ['6181', '8223', '9454'];

// Runway counts only money that can pay bills: Personal checking + savings +
// FIG operating. Never Trust (client funds), never investments.
export const LIQUID_MASKS = ['2634', '2564', '1758'];

export const RUNWAY_WINDOW     = 6;   // trailing FULL months averaged for burn
export const RUNWAY_MIN_MONTHS = 3;   // fewer full months than this → no runway

// Finance_Balances keeps one row per account per day. The header shows the
// latest Date present — one snapshot, so a single "as of" covers every tile.
// An account with no row on that date is simply absent from byMask.
export function latestBalances(rows) {
  let asOf = '';
  for (const r of rows || []) {
    const d = (r.Date || '').slice(0, 10);   // string-safe — no new Date()
    if (d > asOf) asOf = d;
  }
  if (!asOf) return null;

  const byMask = {};
  let trust = null;
  for (const r of rows) {
    if ((r.Date || '').slice(0, 10) !== asOf) continue;
    if (r.Context === 'Trust') trust = r;
    else if (r.Mask) byMask[r.Mask] = r;
  }
  return { asOf, byMask, trust };
}

// "Chase Personal Savings (2564)" → "Chase Personal Savings" — the tile prints
// the mask on its own line, so the copy baked into some titles is dropped.
export function accountName(row) {
  return (row?.Title || '').replace(/\s*\(\d{4}\)\s*$/, '');
}

export function shiftMonth(yyyymm, delta) {
  const [y, m] = yyyymm.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

// Average monthly spend over the trailing RUNWAY_WINDOW full months. A full
// month is a calendar month before the current one that the data covers: the
// month in progress is never counted, and neither is a month before the first
// transaction or after the last (a stale sync must not read as $0 of spend).
// Returns null when fewer than RUNWAY_MIN_MONTHS full months exist.
export function trailingBurn(transactions, isSpend, today = new Date()) {
  let first = '', last = '';
  for (const t of transactions || []) {
    const m = t.Date?.slice(0, 7);
    if (!m) continue;
    if (!first || m < first) first = m;
    if (m > last) last = m;
  }
  if (!first) return null;

  const current = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const full = new Set();
  for (let i = 1; i <= RUNWAY_WINDOW; i++) {
    const m = shiftMonth(current, -i);
    if (m >= first && m <= last) full.add(m);
  }
  if (full.size < RUNWAY_MIN_MONTHS) return null;

  let total = 0;
  for (const t of transactions) {
    if (full.has(t.Date?.slice(0, 7)) && isSpend(t)) total += t.Amount ?? 0;
  }
  return { burn: total / full.size, months: full.size };
}

// What a cash account can actually spend: the bank's Available balance, which
// already nets out pending charges (Chase's Current does not — checked
// 2026-10-11: 2634 Current 13,656.71, Available 12,692.24, gap ≈ pending).
// Falls back to Current for a row with no Available (older snapshots).
export function cashBalance(row) {
  if (!row) return null;
  if (Number.isFinite(row.Available)) return row.Available;
  return Number.isFinite(row.Current) ? row.Current : null;
}

// The cash Runway runs on: Available (cashBalance) on the three liquid
// accounts. Null when it can't be stated honestly: any of the three has no
// balance in the snapshot. (How long it lasts is forwardRunway, in recurring.js.)
export function liquidCash(latest) {
  const vals = LIQUID_MASKS.map(m => cashBalance(latest?.byMask[m]));
  return vals.every(v => Number.isFinite(v))
    ? vals.reduce((s, v) => s + v, 0)
    : null;
}
